/**
 * Incident Reporting API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Incident,
  IncidentListParams,
  IncidentListResponse,
  IncidentStats,
  IncidentStatistics,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  StartInvestigationRequest,
  CompleteInvestigationRequest,
  AddCorrectiveActionRequest,
  UpdateCorrectiveActionRequest,
  AddWitnessRequest,
} from '../types/incident';

/**
 * Incident API Endpoints
 */
export const incidentApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // INCIDENT CRUD
    // ============================================================================

    // List incidents for a project
    getIncidents: builder.query<
      ApiResponse<IncidentListResponse>,
      { projectId: string; params?: IncidentListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/incidents`,
        params,
      }),
      providesTags: ['Incident'],
    }),

    // Get single incident
    getIncident: builder.query<ApiResponse<Incident>, { projectId: string; incidentId: string }>({
      query: ({ projectId, incidentId }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}`,
      }),
      providesTags: (_result, _error, { incidentId }) => [{ type: 'Incident', id: incidentId }],
    }),

    // Create incident
    createIncident: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; data: CreateIncidentRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/incidents`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Incident'],
    }),

    // Update incident
    updateIncident: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; data: UpdateIncidentRequest }
    >({
      query: ({ projectId, incidentId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // Delete incident
    deleteIncident: builder.mutation<ApiResponse<void>, { projectId: string; incidentId: string }>({
      query: ({ projectId, incidentId }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Incident'],
    }),

    // ============================================================================
    // STATISTICS
    // ============================================================================

    // Get quick stats
    getIncidentStats: builder.query<ApiResponse<IncidentStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/incidents/stats`,
      }),
      providesTags: ['Incident'],
    }),

    // Get detailed statistics
    getIncidentStatistics: builder.query<
      ApiResponse<IncidentStatistics>,
      { projectId: string; dateFrom?: string; dateTo?: string }
    >({
      query: ({ projectId, dateFrom, dateTo }) => ({
        url: `/projects/${projectId}/incidents/statistics`,
        params: { dateFrom, dateTo },
      }),
      providesTags: ['Incident'],
    }),

    // ============================================================================
    // INVESTIGATION WORKFLOW
    // ============================================================================

    // Start investigation
    startInvestigation: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; data?: StartInvestigationRequest }
    >({
      query: ({ projectId, incidentId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/investigate`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // Complete investigation
    completeInvestigation: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; data?: CompleteInvestigationRequest }
    >({
      query: ({ projectId, incidentId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/investigation/complete`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // ============================================================================
    // CORRECTIVE ACTIONS
    // ============================================================================

    // Add corrective action
    addCorrectiveAction: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; data: AddCorrectiveActionRequest }
    >({
      query: ({ projectId, incidentId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/actions`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // Update corrective action
    updateCorrectiveAction: builder.mutation<
      ApiResponse<Incident>,
      {
        projectId: string;
        incidentId: string;
        actionId: string;
        data: UpdateCorrectiveActionRequest;
      }
    >({
      query: ({ projectId, incidentId, actionId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/actions/${actionId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // Complete corrective action
    completeCorrectiveAction: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; actionId: string }
    >({
      query: ({ projectId, incidentId, actionId }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/actions/${actionId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // Verify corrective action
    verifyCorrectiveAction: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; actionId: string }
    >({
      query: ({ projectId, incidentId, actionId }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/actions/${actionId}/verify`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // ============================================================================
    // WITNESSES
    // ============================================================================

    // Add witness
    addWitness: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string; data: AddWitnessRequest }
    >({
      query: ({ projectId, incidentId, data }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/witnesses`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),

    // ============================================================================
    // CLOSE INCIDENT
    // ============================================================================

    // Close incident
    closeIncident: builder.mutation<
      ApiResponse<Incident>,
      { projectId: string; incidentId: string }
    >({
      query: ({ projectId, incidentId }) => ({
        url: `/projects/${projectId}/incidents/${incidentId}/close`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { incidentId }) => [
        'Incident',
        { type: 'Incident', id: incidentId },
      ],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetIncidentsQuery,
  useGetIncidentQuery,
  useCreateIncidentMutation,
  useUpdateIncidentMutation,
  useDeleteIncidentMutation,
  // Statistics
  useGetIncidentStatsQuery,
  useGetIncidentStatisticsQuery,
  // Investigation
  useStartInvestigationMutation,
  useCompleteInvestigationMutation,
  // Corrective Actions
  useAddCorrectiveActionMutation,
  useUpdateCorrectiveActionMutation,
  useCompleteCorrectiveActionMutation,
  useVerifyCorrectiveActionMutation,
  // Witnesses
  useAddWitnessMutation,
  // Close
  useCloseIncidentMutation,
} = incidentApi;

/**
 * Re-export types for convenience
 */
export type { IncidentListParams, IncidentStats, IncidentStatistics } from '../types/incident';
